import { WheelEvent, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { IGift } from "@/api_framework/api_modals/GiftConfig";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useGiftSetupAPI } from "@/app/hooks/api_hooks/gift/useGiftSetupAPI";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { BackLink } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/components";
import { cardClassName } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { DEFAULT_VALUES, buildGiftPayload, toFormValues } from "../formUtils";
import { IGiftFormFields, toClinicSelectItems } from "../interface";
import { ClaimConditionsCard, DisplayCard } from "./DisplayCard";
import { EligibilityCard } from "./EligibilityCard";
import { GiftItemsCard } from "./GiftItemsCard";
import { GeneralDetailsCard } from "./GeneralDetailsCard";

/**
 * Browsers step a focused number input when the mouse wheel scrolls over it, silently changing values like
 * limits or discounts. Dropping focus on wheel keeps the value and lets the page scroll as usual.
 */
const blurFocusedNumberInput = (event: WheelEvent<HTMLFormElement>) => {
  const target = event.target;
  if (target instanceof HTMLInputElement && target.type === "number" && target === document.activeElement) {
    target.blur();
  }
};

/** Save stays disabled until every required field is filled and valid. */
const FormActions = ({ isSaving, onCancel }: { isSaving: boolean; onCancel: () => void }) => {
  const { isValid } = useFormState<IGiftFormFields>();
  return (
    <div className="sticky bottom-0 z-10 mt-4 flex items-center justify-end gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
      <button
        type="button"
        className="rounded-lg border border-slate-200 bg-white px-8 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
        onClick={onCancel}
        disabled={isSaving}
      >
        Back
      </button>
      <button
        type="submit"
        className="rounded-lg bg-[#193A6A] px-8 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52] disabled:cursor-not-allowed disabled:opacity-60"
        disabled={isSaving || !isValid}
        title={isValid ? undefined : "Fill all required fields to save"}
      >
        {isSaving ? "Saving…" : "Save Gift"}
      </button>
    </div>
  );
};

const GiftCreation = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const giftId = params.get("gift_id");
  const isEditMode = Boolean(giftId);

  const { giftFormat, isFormatLoading, isFetching, isSaving, fetchGift, fetchGiftFormat, fetchVariantNames, saveGift } =
    useGiftSetupAPI();
  const { execute: fetchClinicList, clinicList } = useClinicListAPI();
  const [isClinicListLoading, setIsClinicListLoading] = useState<boolean>(true);
  const clinicItems = useMemo(() => toClinicSelectItems(clinicList), [clinicList]);

  const [savedGift, setSavedGift] = useState<IGift | null>(null);
  const [isMissingGift, setIsMissingGift] = useState<boolean>(false);

  const formHook = useForm<IGiftFormFields>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: DEFAULT_VALUES,
  });
  const { getValues, handleSubmit, reset, setValue } = formHook;

  useEffect(() => {
    fetchGiftFormat();
  }, [fetchGiftFormat]);

  // All org clinics, for picking included/excluded clinics by name.
  useEffect(() => {
    fetchClinicList().finally(() => setIsClinicListLoading(false));
  }, [fetchClinicList]);

  // Dropdown options come only from the `format` action; until it responds the dropdowns show "Loading…".
  const allowedStatuses = giftFormat?.allowed_statuses ?? [];
  const allowedGiftTypes = giftFormat?.allowed_gift_types ?? [];
  const allowedOperations = giftFormat?.allowed_operations ?? [];
  const allowedDiscountTypes = giftFormat?.allowed_discount_types ?? [];
  const allowedPlatforms = giftFormat?.allowed_platforms ?? [];

  useEffect(() => {
    if (!giftId) return;
    let isCurrent = true;
    fetchGift(giftId).then(async (gift) => {
      if (!isCurrent) return;
      setIsMissingGift(!gift);
      setSavedGift(gift);
      if (!gift) return;
      const formValues = toFormValues(gift);
      reset(formValues);

      // Product condition ids come back without names, so look them up to show product names on the chips.
      const { included_ids, excluded_ids } = formValues.eligibility.product;
      const unnamedIds = [...included_ids, ...excluded_ids].filter((id) => !formValues.id_names[id]);
      const names = await fetchVariantNames(unnamedIds);
      if (isCurrent && Object.keys(names).length) setValue("id_names", { ...getValues("id_names"), ...names });
    });
    return () => {
      isCurrent = false;
    };
  }, [giftId, fetchGift, fetchVariantNames, getValues, reset, setValue]);

  const listUrl = getRouteKey("GIFT_SETUP_LIST", "url");

  const handleCancel = () => {
    reset(DEFAULT_VALUES);
    navigate(listUrl);
  };

  const onSubmit = async (data: IGiftFormFields) => {
    const payload = buildGiftPayload(data, savedGift);
    const savedGiftId = await saveGift(payload, giftId ?? undefined);
    if (savedGiftId !== null) navigate(listUrl);
  };

  const onInvalid = () => toast.error("Fix the highlighted fields before saving.");

  if (isEditMode && isMissingGift) {
    return (
      <div className="px-5 py-4">
        <BackLink label="Back to gifts" onClick={handleCancel} />
        <div className={`${cardClassName} px-5 py-10 text-center`}>
          <p className="text-sm font-semibold text-slate-700">This gift could not be opened.</p>
          <p className="mt-1 text-xs text-slate-500">It may have been deleted. Open it again from the gift list.</p>
        </div>
      </div>
    );
  }

  if (isEditMode && (isFetching || !savedGift)) {
    return (
      <div className="bg-white px-5 py-4">
        <SkeletonType1 />
        <SkeletonType1 className="mt-8" />
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      <FormProvider {...formHook}>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} onWheelCapture={blurFocusedNumberInput} noValidate>
          <BackLink label="Back to gifts" onClick={handleCancel} />
          <div className="space-y-4">
            <GeneralDetailsCard
              allowedStatuses={allowedStatuses}
              allowedGiftTypes={allowedGiftTypes}
              isFormatLoading={isFormatLoading}
            />
            <EligibilityCard
              allowedOperations={allowedOperations}
              isFormatLoading={isFormatLoading}
              clinicItems={clinicItems}
              isClinicListLoading={isClinicListLoading}
            />
            <GiftItemsCard allowedDiscountTypes={allowedDiscountTypes} isFormatLoading={isFormatLoading} />
            <DisplayCard allowedPlatforms={allowedPlatforms} isFormatLoading={isFormatLoading} />
            <ClaimConditionsCard allowedPlatforms={allowedPlatforms} isFormatLoading={isFormatLoading} />
          </div>
          <FormActions isSaving={isSaving} onCancel={handleCancel} />
        </form>
      </FormProvider>
    </div>
  );
};

export default GiftCreation;
